// Audit-package ZIP bundling.
//
// Packs:
//   /export.csv                                — full row data
//   /summary.pdf                               — auditor-friendly summary
//   /receipts/<expense_or_invoice_id>/<file>   — raw receipt bytes
//   /MANIFEST.txt                              — human-readable index
//
// File-name-collision strategy: if multiple receipts attach to the same row,
// they're disambiguated by appending an index. If a receipt's filename has
// no extension, we infer one from the mime type.

import { receiptStore } from "../receipts";
import type { Receipt } from "../receipts/types";
import { buildExpensesCsv, buildExportFilename } from "./csv";
import { generateExpensesPdf, filterExpensesForExport } from "./pdf";
import type { ExportDataSources, ExportRequest } from "./types";
import { buildZip, type ZipInput } from "./zipWriter";

const EXT_BY_MIME: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "application/pdf": "pdf",
};

function safeBaseName(name: string): string {
  // Strip path separators and control chars; keep dots and dashes.
  return name.replace(/[/\\<>:"|?* -]/g, "_").trim() || "file";
}

function ensureExt(name: string, mimeType: string): string {
  const lastDot = name.lastIndexOf(".");
  if (lastDot > 0 && lastDot < name.length - 1) return name;
  const inferred = EXT_BY_MIME[mimeType.toLowerCase()] ?? "bin";
  return `${name}.${inferred}`;
}

async function fetchReceiptBytes(
  receipt: Receipt,
): Promise<Uint8Array | null> {
  // Refresh the URL first to dodge expired signed URLs (signed URLs from the
  // initial snapshot may be hours old by the time the user clicks Export).
  const url = (await receiptStore.refreshUrl(receipt.id)) ?? receipt.url;
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    return new Uint8Array(buf);
  } catch {
    return null;
  }
}

export interface BuildAuditPackageResult {
  zipBytes: Uint8Array;
  receiptCount: number;
  rowCount: number;
  missingReceipts: number;
}

export async function buildAuditPackage(
  sources: ExportDataSources,
  request: ExportRequest,
  fullReceipts: Receipt[],
): Promise<BuildAuditPackageResult> {
  // 1) CSV — same row set as a regular CSV export.
  const csv = buildExpensesCsv(sources, { ...request, includeReceipts: true });

  // 2) PDF — same render as a regular PDF export.
  const filteredExpenses = filterExpensesForExport(sources.expenses, {
    dateFrom: request.dateFrom,
    dateTo: request.dateTo,
    categoryIds: request.categoryIds,
    userId: request.userId ?? null,
  });
  const pdfBlob = await generateExpensesPdf({
    expenses: filteredExpenses,
    categories: sources.categories,
    taxPolicies: sources.taxPolicies,
    orgName: "Proofly",
    dateRange: { from: request.dateFrom, to: request.dateTo },
  });
  const pdfBytes = new Uint8Array(await pdfBlob.arrayBuffer());

  // 3) Collect receipts to bundle. The CSV's row set is the source of truth
  // for what's "in scope" — only fetch receipts attached to those rows.
  const inScopeExpenseIds = new Set(filteredExpenses.map((e) => e.id));
  const inScopeInvoiceIds = new Set(
    sources.invoices
      .filter((inv) => {
        if (request.dateFrom && inv.issuedOn < request.dateFrom) return false;
        if (request.dateTo && inv.issuedOn > request.dateTo) return false;
        if (request.userId && inv.submittedBy !== request.userId) return false;
        return true;
      })
      .map((inv) => inv.id),
  );

  const receiptsToBundle = fullReceipts.filter((r) => {
    if (r.linkedExpenseId && inScopeExpenseIds.has(r.linkedExpenseId)) {
      return true;
    }
    if (r.linkedInvoiceId && inScopeInvoiceIds.has(r.linkedInvoiceId)) {
      return true;
    }
    return false;
  });

  // 4) Group by row id so collisions get a numeric suffix.
  const grouped = new Map<string, Receipt[]>();
  for (const r of receiptsToBundle) {
    const key = r.linkedExpenseId
      ? `expense_${r.linkedExpenseId}`
      : `invoice_${r.linkedInvoiceId ?? "unknown"}`;
    const list = grouped.get(key) ?? [];
    list.push(r);
    grouped.set(key, list);
  }

  // 5) Fetch bytes in parallel. Missing files are skipped (counted but not
  // fatal — partial audit packages are more useful than no package at all).
  const fileEntries: ZipInput[] = [];
  const manifestLines: string[] = [
    "Proofly Audit Package",
    `Generated: ${new Date().toISOString()}`,
    `Date range: ${request.dateFrom ?? "(all)"} to ${request.dateTo ?? "(all)"}`,
    "",
    "Files:",
    "  export.csv     — row data (matches the CSV export)",
    "  summary.pdf    — auditor-facing summary",
    "  receipts/...   — raw receipt files attached to in-scope rows",
    "",
    "Receipt index:",
  ];

  let missing = 0;
  let bundled = 0;

  for (const [groupKey, group] of grouped) {
    const bytes = await Promise.all(group.map(fetchReceiptBytes));
    for (let i = 0; i < group.length; i += 1) {
      const r = group[i]!;
      const data = bytes[i];
      const safe = safeBaseName(r.filename || `${r.id}.bin`);
      const named = ensureExt(safe, r.mimeType || "");
      const path =
        group.length > 1
          ? `receipts/${groupKey}/${i + 1}_${named}`
          : `receipts/${groupKey}/${named}`;
      if (!data || data.length === 0) {
        missing += 1;
        manifestLines.push(`  [MISSING]  ${path}  (id=${r.id})`);
        continue;
      }
      fileEntries.push({ name: path, data });
      manifestLines.push(`  ${path}  (${data.length} bytes, id=${r.id})`);
      bundled += 1;
    }
  }

  if (bundled === 0) {
    manifestLines.push("  (no receipts attached to in-scope rows)");
  }

  // 6) Assemble. CSV + PDF + manifest sit at the root; receipts under /receipts.
  const allEntries: ZipInput[] = [
    { name: "export.csv", data: csv.content },
    { name: "summary.pdf", data: pdfBytes },
    { name: "MANIFEST.txt", data: manifestLines.join("\n") },
    ...fileEntries,
  ];

  const zipBytes = buildZip(allEntries);

  return {
    zipBytes,
    receiptCount: bundled,
    rowCount: csv.rowCount,
    missingReceipts: missing,
  };
}

export function buildAuditPackageFilename(createdAt: Date): string {
  return buildExportFilename("zip", createdAt);
}
